// SPDX-License-Identifier: AGPL-3.0-only

import type { TenantRole } from "./tenants.js";
import { requestJson } from "./http.js";

export interface TenantMember {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly userStatus: "active" | "disabled" | "deleted";
  readonly membershipStatus: TenantMembershipStatus;
  readonly role: TenantRole;
  readonly joinedAt: string;
  readonly disabledAt: string | null;
}

export type TenantMembershipStatus = "active" | "disabled";

export interface AddTenantMemberInput {
  readonly email: string;
  readonly role: TenantRole;
}

const TENANT_ROLES: ReadonlySet<string> = new Set<string>([
  "tenant_admin",
  "probe_admin",
  "operator",
  "viewer",
]);
const USER_STATUSES: ReadonlySet<string> = new Set<string>(["active", "disabled", "deleted"]);
const MEMBERSHIP_STATUSES: ReadonlySet<string> = new Set<string>(["active", "disabled"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Tenant member response field ${fieldName} is invalid`);
  }
  return value;
}

function readMember(payload: unknown, fieldName: string): TenantMember {
  if (!isRecord(payload)) {
    throw new Error(`Tenant member response field ${fieldName} is invalid`);
  }
  const role: string = readString(payload.role, `${fieldName}.role`);
  const userStatus: string = readString(payload.userStatus, `${fieldName}.userStatus`);
  const membershipStatus: string = readString(
    payload.membershipStatus,
    `${fieldName}.membershipStatus`,
  );
  if (
    !TENANT_ROLES.has(role) ||
    !USER_STATUSES.has(userStatus) ||
    !MEMBERSHIP_STATUSES.has(membershipStatus) ||
    (payload.disabledAt !== null && typeof payload.disabledAt !== "string")
  ) {
    throw new Error(`Tenant member response field ${fieldName} is invalid`);
  }
  return {
    userId: readString(payload.userId, `${fieldName}.userId`),
    email: readString(payload.email, `${fieldName}.email`),
    displayName: readString(payload.displayName, `${fieldName}.displayName`),
    userStatus: userStatus as "active" | "disabled" | "deleted",
    membershipStatus: membershipStatus as TenantMembershipStatus,
    role: role as TenantRole,
    joinedAt: readString(payload.joinedAt, `${fieldName}.joinedAt`),
    disabledAt: payload.disabledAt,
  };
}

export async function listTenantMembers(tenantId: string): Promise<readonly TenantMember[]> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/members`,
  );
  if (!isRecord(payload) || !Array.isArray(payload.members)) {
    throw new Error("Tenant member list response is invalid");
  }
  return payload.members.map(
    (member: unknown, index: number): TenantMember => readMember(member, `members.${index}`),
  );
}

export async function addTenantMember(
  tenantId: string,
  input: AddTenantMemberInput,
): Promise<TenantMember> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/members`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return readMember(payload, "member");
}

export async function updateTenantMemberRole(
  tenantId: string,
  userId: string,
  role: TenantRole,
): Promise<TenantMember> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    { method: "PATCH", body: JSON.stringify({ role }) },
  );
  return readMember(payload, "member");
}

export async function removeTenantMember(tenantId: string, userId: string): Promise<void> {
  await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export async function changeTenantMemberStatus(
  tenantId: string,
  userId: string,
  status: TenantMembershipStatus,
): Promise<TenantMember> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/status`,
    { method: "PATCH", body: JSON.stringify({ status }) },
  );
  return readMember(payload, "member");
}
